import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import EmailSendProgress from 'components/EmailSendProgress'
import { Tabs, Loader } from '@driftt/tide-core'
import { delay } from 'utils'
import moment from 'moment'
import { Button, Div, P } from '@driftt/tide-core'
import { equals } from 'ramda'
import { access } from '@driftt/traffic-control'

import { getReportDataByID, getDollarsInfluenced, getPlaybookSendProgress } from 'api/playbooks'
import EmailPerformance from 'components/Reports/EmailPerformance/EmailPerformanceTab'
import PeopleTab from 'components/Reports/EmailPerformance/PeopleTab'
import { getIsOneTimeEmailPlaybook } from 'constants/playbook_definitions'

import './styles.css'

class EmailReport extends Component {
  constructor(props) {
    super(props)

    this.state = {
      totalStats: null,
      playbook: null,
      isLoading: true,
      hasLoaded: false,
      emailSendProgress: null,
      progressDismissed: false,
    }

  }

  componentDidMount() {
    this.getPlaybookStats()
    this.getEmailSendProgress()
  }

  componentDidUpdate(prevProps) {
    if (!equals(this.props, prevProps)) {
      this.setState({ hasLoaded: true, isLoading: true, totalStats: null })
      this.getPlaybookStats()
      this.getEmailSendProgress()
    }
  }

  onProgressDismiss = () => {
    this.setState({ progressDismissed: true })
  }

  getEmailSendProgress = () => {
    this.intervaledProgressFetchId = setInterval(this.fetchEmailSendAtInterval, 4000)
    this.fetchEmailSendAtInterval()
  }

  shouldCancelInterval = progressData => {
    const twoDaysAgo = moment().subtract(2, 'd')

    return (progressData.totalEnqueued === progressData.totalDequeued && !!progressData.totalDequeued) ||
      progressData.doneSending ||
      progressData.emailSentAt < twoDaysAgo.valueOf()
  }

  stopFetchingProgress = () => {
    clearInterval(this.intervaledProgressFetchId)
    this.intervaledProgressFetchId = null
  }

  fetchEmailSendAtInterval = async () => {
    const { playbook } = this.props
    if (getIsOneTimeEmailPlaybook(playbook)) {
      try {
        const { data } = await getPlaybookSendProgress(playbook.id)

        if (this.shouldCancelInterval(data)) {
          this.stopFetchingProgress()
        }
        this.setState({emailSendProgress: data})
      } catch(error) {
        console.warn(error)
        this.stopFetchingProgress()
      }
    } else {
      this.stopFetchingProgress()
    }
    this.setHasLoaded()
  }

  refetchStats = () => {
    this.setState({
      isLoading: true,
    }, this.getPlaybookStats)
  }

  getPlaybookStats = async () => {
    const { currentDateRange, previousDateRange, playbook } = this.props
    const { id } = playbook
    const [ startDate, endDate ] = currentDateRange
    const [ prevStartDate, prevEndDate ] = previousDateRange

    try {
      const { data } = await getReportDataByID(id, { startDate, endDate, prevStartDate, prevEndDate })
      this.setState({totalStats: data}, this.getSalesForceData)
    } catch (error) {
      console.log(error)
    }
  }

  getSalesForceData = async () => {
    const { playbook, salesForceRevenue, currentDateRange, previousDateRange } = this.props
    const { id } = playbook
    const [ startDate, endDate ] = currentDateRange
    const [ prevStartDate, prevEndDate ] = previousDateRange

    if (!salesForceRevenue) {
      this.setHasLoaded()
      return
    }

    try {
      const { data } = (await getDollarsInfluenced(id, {startDate, endDate, prevStartDate, prevEndDate}))
      this.setState({
        totalSFRevenue: data,
        isLoading: false,
        sfError: false,
      })
    } catch (e) {
      this.setState({ sfError: true })
      console.log(e)
    } finally {
      this.setHasLoaded()
    }
  }

  setHasLoaded = async () => {
    const setLoaded = () => {
      this.setState({
        hasLoaded: true
      })
    }

    const setComplete = () => {
      this.setState({
        isLoading: false
      })
    }

    setLoaded()
    delay(setComplete, 1000)
  }

  render() {
    const { totalStats, isLoading, hasLoaded, totalSFRevenue, emailSendProgress, progressDismissed, sfError } = this.state
    const { playbook, salesForceRevenue, currentDateRange, previousDateRange, hasEmailSendProgress } = this.props
    const playbookID = playbook.id

    const showRefreshMetrics = !!this.intervaledProgressFetchId

    const TAB_GROUP = [{
      title: 'Performance',
      component: () => <EmailPerformance
                          id={playbookID}
                          data={totalStats}
                          playbook={playbook}
                          dateRanges={{current: currentDateRange, previous: previousDateRange}}
                          salesForceRevenue={salesForceRevenue}
                          totalSFRevenue={totalSFRevenue}
                          sfError={sfError}
                          playbookReportType="email"
                        />
      },
      {
        title: 'People',
        component: () => <PeopleTab
                            id={playbookID}
                            playbook={playbook}
                            dates={currentDateRange}
                          />
      }]

    return (
      <Div className="playbook-reports-content-container">
        {isLoading &&
          <Div className="playbook-report-loader">
            <Loader height={200} width={200} />
          </Div>
        }
        {hasLoaded &&
          <Fragment>
            {hasEmailSendProgress &&  <EmailSendProgress emailSendProgress={emailSendProgress} onDismiss={this.onProgressDismiss} dismissed={progressDismissed} />}
            {showRefreshMetrics && <P className="reports-refresh-helptext">Report data does not refresh automatically. Click here to refresh <Button type="subtle" onClick={this.refetchStats}>Refresh</Button></P>}
            <Tabs data={TAB_GROUP} activeTab={0} />
          </Fragment>
        }
      </Div>
    )
  }
}

const mapStateToProps = (state) => {
  return {
    salesForceRevenue: state.org.properties.salesforceRevenueMetricsEnabled,
    hasEmailSendProgress: access.hasGates('has_email_send_progress_v2')
  }
}

export default connect(mapStateToProps, null)(EmailReport)
